import React, { useMemo } from 'react';
import { DownloadIcon, MailIcon } from 'lucide-react';
import { policyMeta, policySections } from '../data/privacyPolicy';
import { PolicyContents } from '../components/legal/PolicyContents';
import { PolicySectionBody } from '../components/legal/PolicySectionBody';
import { BackToTop } from '../components/legal/BackToTop';
import { useActiveSection } from '../hooks/useActiveSection';
const metaLinkStyles = 'inline-flex items-center gap-2 text-sm font-medium text-purple-700 underline underline-offset-4 decoration-purple-300 hover:text-purple-900 hover:decoration-purple-700 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory rounded';
export function PrivacyPolicyPage() {
  const sectionIds = useMemo(() => policySections.map(section => section.id), []);
  const activeId = useActiveSection(sectionIds);
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-457" data-mp="29rmt9grl7m36">
      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 px-6 py-6 flex justify-between items-center max-w-7xl mx-auto bg-ivory/95 backdrop-blur-sm border-b border-charcoal/5" data-id="element-458" data-mp="24ricrqqoircy">
        <a href="/" aria-label="Fem Entity home" data-id="element-459" data-mp="2eq6hjntnl9yp">
          <img src="https://cdn.magicpatterns.com/uploads/xpYure8muF9bisffxXgVg2/fementity.png" alt="Fem Entity" className="h-8 md:h-10" data-id="element-460" data-mp="2erhhtdnw3zl2" />
        </a>
        <div className="hidden md:flex space-x-8 text-sm font-medium text-charcoal/80" data-id="element-461" data-mp="1m3vi4ghs9f3h">
          <a href="/" className="hover:text-purple-700 transition-colors" data-id="element-462" data-mp="20shvwdrrhysl">
            Home
          </a>
          <a href="/practitioners" className="hover:text-purple-700 transition-colors" data-id="element-463" data-mp="1vynbt988vhsz">
            Practitioners
          </a>
          <a href="/investors" className="hover:text-purple-700 transition-colors" data-id="element-464" data-mp="13ccfwbvl252i">
            Investors
          </a>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-465" data-mp="1x11sye27ir2y">
        {/* Title area */}
        <header className="pt-16 md:pt-24 pb-10 md:pb-14 border-b border-charcoal/10" data-id="element-466" data-mp="21s09ner3xr6q">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-5" data-id="element-467" data-mp="21u372julamlh">
            {policyMeta.company}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-charcoal leading-[1.05] tracking-tight" data-id="element-468" data-mp="1a1lyhqahasyr">
            {policyMeta.title}
          </h1>
          <p className="mt-6 max-w-[62ch] text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-469" data-mp="1e4igbes72l1n">
            {policyMeta.summary}
          </p>

          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-id="element-470" data-mp="1o0vhexq2k16f">
            <div className="flex items-baseline gap-2" data-id="element-471" data-mp="2ugxof7w415uq">
              <dt className="text-charcoal/50" data-id="element-472" data-mp="2ugfoo1a8o9i2">Version</dt>
              <dd className="font-medium text-charcoal" data-id="element-473" data-mp="2zez9tzh0dh9d">
                {policyMeta.version.replace('Version ', '')}
              </dd>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-474" data-mp="nu9lgubejuo2">
              <dt className="text-charcoal/50" data-id="element-475" data-mp="iun7ginr2ae7">Effective</dt>
              <dd className="font-medium text-charcoal" data-id="element-476" data-mp="e2c75ep3kml4">September 2026</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-477" data-mp="942bz43dhlzi">
            <a href={policyMeta.pdfUrl} target="_blank" rel="noopener noreferrer" className={metaLinkStyles} data-id="element-478" data-mp="91ofcqt1o1pu">
              <DownloadIcon aria-hidden="true" className="w-4 h-4" data-id="element-479" data-mp="4xe7zzmxwoe" />
              Download the PDF
            </a>
            <a href={`mailto:${policyMeta.email}`} className={metaLinkStyles} data-id="element-480" data-mp="3rayr8e35v0co">
              <MailIcon aria-hidden="true" className="w-4 h-4" data-id="element-481" data-mp="3mbvlssj9qs46" />
              {policyMeta.email}
            </a>
          </div>
        </header>

        {/* Index + body */}
        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14 xl:gap-20 pb-24 md:pb-32" data-id="element-482" data-mp="1kjzdrswb80e5">
          <PolicyContents sections={policySections} activeId={activeId} data-id="element-483" data-mp="1skoshmxv002n" />

          <article className="max-w-[68ch] pt-10 lg:pt-14 divide-y divide-charcoal/10" data-id="element-484" data-mp="1xiqfgiyeojje">
            {policySections.map(section => <PolicySectionBody key={section.id} section={section} data-id="element-485" data-mp="1iky0brwrtx5q" />)}

            <p className="pt-12 text-sm text-charcoal/50" data-id="element-486" data-mp="1nizlqjho68kd">
              © {new Date().getFullYear()} Fem Entity Inc. All rights reserved.{' '}
              {policyMeta.version} — {policyMeta.effectiveDate}.
            </p>
          </article>
        </div>
      </main>

      <BackToTop data-id="element-487" data-mp="zvhls0o9446b" />

      {/* Footer */}
      <footer className="bg-charcoal text-ivory/50 py-8 border-t border-white/10" data-id="element-488" data-mp="zwjav923qr71">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-sm" data-id="element-489" data-mp="14uktjsvm8ohz">
          <p data-id="element-490" data-mp="qw4lnym0ory9">© {new Date().getFullYear()} Fem Entity. All rights reserved.</p>
          <a href="/privacy-policy" aria-current="page" className="text-ivory/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 rounded" data-id="element-491" data-mp="uv3ptgzxckon">
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>;
}